import Sidebar from './Sidebar.jsx';
import Graph1 from './Graph.jsx';
import { BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';

const ModelTab1 = () => {
	let model1Img =
		'https://static.igem.wiki/teams/5066/drylab/model-tab1-1.jpg';
	let model2Img =
		'https://static.igem.wiki/teams/5066/drylab/model-tab1-2.png';
	let model3Img =
		'https://static.igem.wiki/teams/5066/drylab/model-tab1-3.jpeg';
	let model4Img =
		'https://static.igem.wiki/teams/5066/drylab/model-tab1-4.jpeg';
	let model5Img =
		'https://static.igem.wiki/teams/5066/drylab/model-tab1-5.png';
	let model6Img =
		'https://static.igem.wiki/teams/5066/drylab/model-tab1-6.jpg';
	let model7Img =
		'https://static.igem.wiki/teams/5066/drylab/model-tab1-7.jpeg';

	let sections = [
		{ id: 's1', name: 'Objective' },
		{ id: 's2', name: 'The Epidemiological Cycle' },
		{ id: 's3', name: 'The Model' },
		{ id: 's4', name: 'Results and Discussions' },
		{ id: 's5', name: 'Try out the Model!' },
		{ id: 's16', name: 'Methods and Assumptions' },
		{ id: 's6', name: 'Equations and Parameters' },
		{ id: 's7', name: 'Limitations and Future Directions' },
		{ id: 's8', name: 'References' },

	];

	return (
		<div className='flex'>
			<Sidebar sections={sections} />

			{/* Main Content */}
			<div className='flex-col w-full lg:p-[50px] p-[5px] my-[10px] space-y-16 rounded-2xl bg-white shadow-[-2px_4px_8px_-2px_rgba(0,0,0,0.2)]'>
				<div className='pb-12'>
					<section id='s1'>
						<h2 className='text-3xl font-bold text-[#C94C67]'>
							Objective
						</h2>
						<div className='mt-4'>
							<p className='mt-2 text-gray-700'>
								We aim to build a system of ordinary
								differential equations to simulate the complex
								process of dengue fever transmission between
								vectors (mosquitoes) and hosts (humans). We then
								want to show the direct impact of implementing
								our sticky ovitraps containing our synthetic
								larvicidal toxin within a location with a
								certain human population by revealing a decrease
								in the infected human population from micro
								changes in the larva stages of the mosquito life
								cycle.
							</p>
						</div>
					</section>
				</div>

				<div className='pb-12'>
					<section id='s2'>
						<h2 className='text-3xl font-bold text-[#C94C67]'>
							The Epidemiological Cycle
						</h2>
						<p className='mt-2 text-gray-700'>
							The epidemiological cycle begins with the female
							mosquito biting an infectious individual, causing
							its own infection. After 7 to 10 days, the mosquito
							transmits the disease (the extrinsic incubation
							period, which starts when the mosquitoes ingest the
							dengue virus-infected blood and ends with the
							mosquitoes’ infectious stage). Once infected, the
							mosquito will carry the dengue fever virus for the
							rest of its lifespan. Then, the infectious mosquito
							is able to transmit the virus by biting and feeding
							on a susceptible human. Then, for a period of about
							4 to 6 days after the transmission of the virus,
							known as the intrinsic incubation period, the dengue
							virus can progress from asymptomatic to classical
							dengue fever. The development of the symptoms varies
							between 3 and 7 days, which corresponds to the
							infectious period. After recovery, the host develops
							lifelong immunity to this specific strain of dengue
							fever. This concludes the epidemiological cycle of
							dengue fever transmission between vectors and hosts.
							Now, we need to develop a mathematical model to
							simulate this process!
						</p>
					</section>
				</div>

				<div className='pb-12'>
					<section id='s3'>
						<h2 className='text-3xl font-bold text-[#C94C67]'>
							The Model
						</h2>
						<p className='mt-2 text-gray-700'>
							We expand the commonly used SEIR model
							(susceptible–exposed–infectious–recovered) to a
							SEI-SEIR model. It is separated into two parts, with
							the top row representing mosquitoes (vector) and the
							bottom row representing humans (host). This
							bifurcation is employed because both the vector and
							the host contract the virus and go through a period
							of latency in which the individual has the virus but
							is not capable of transmitting it.
						</p>
						<img src={model1Img} alt='result-pic'></img>
						<p className='mt-2 text-gray-700'>
							The mosquito population is represented by 6
							compartments: eggs (E), larvae (L), pupae (P), adult
							mosquitoes susceptible to dengue (
							<sub>
								M<sub>s</sub>
							</sub>
							), mosquitoes that are infected but not infectious (
							<sub>
								M<sub>e</sub>
							</sub>
							), infectious mosquitoes (
							<sub>
								M<sub>i</sub>
							</sub>
							). The total mosquito population, M(t) ={' '}
							M<sub>s</sub>
							{' '}
							+{' '}

							M<sub>e</sub>
							{' '}
							+{' '}

							M<sub>i</sub>
							.
						</p>
						<p className='mt-2 text-gray-700'>
							Transition rates for pre-adult mosquitoes are
							represented by different <strong>σ</strong>: σ
							<sub>e</sub>E (egg to larva), σ<sub>l</sub>L (larva
							to pupa), and σ<sub>p</sub>P (pupa to susceptible
							mosquito). The rate at which adult mosquitoes
							transition from the Susceptible to the Exposed
							stage, β<sub>M</sub>M<sub>s</sub> <sup>i</sup> / N,
							and the rate at which they transition from the
							Exposed to Infected stage is Y<sub>M</sub>M
							<sub>e</sub>. The mortality rates of the mosquito
							population are represented through the symbol{' '}
							<strong>μ</strong>: μ<sub>e</sub>, μ<sub>l</sub>, μ
							<sup>'</sup>
							<sub>l</sub>, and μ<sub>p</sub> being the mortality
							rates of eggs, larvae, larvae death rate by
							larvicidal toxin, and pupa, respectively.
						</p>
						<p className='mt-2 text-gray-700'>
							The human population is represented by 4
							compartments, in which “s”, “e”, “i”, and “r”
							represent the susceptible, exposed, infectious, and
							recovered fractions of the human population. The
							transition from the susceptible population to the
							exposed state is represented by β<sub>h</sub>S
							<sub>s</sub>M<sub>i</sub> / M. The transition from
							the exposed population to the infectious state is
							rate Y<sub>h</sub>e. Infected individuals transition
							from the infected to the removed stage with the
							recovery rate σ<sub>h</sub>i.
						</p>
						<p className='mt-2 text-gray-700'>
							Here comes the most intriguing part of this model:
							the intertwined cross-transmission between vectors
							and hosts (evident from Figure 1). Following the red
							diagonal arrow from{' '}
							<span style={{ fontWeight: 'bold' }}>
								"M<sub>s</sub>"
							</span>{' '}
							to <span style={{ fontWeight: 'bold' }}>"i"</span>,
							the susceptible mosquitoes get infected with the
							dengue virus when they feed from infected human
							blood; this is represented by the infection rate
							<span style={{ fontStyle: 'italic' }}>
								{' '}
								β<sub>M</sub> <sup>i(t)</sup>/N M<sub>s</sub>(t)
							</span>
							. Similarly, the other red diagonal arrow from{' '}
							<span style={{ fontWeight: 'bold' }}>
								"M<sub>i</sub>"
							</span>{' '}
							to <span style={{ fontWeight: 'bold' }}>"s"</span>
							indicates the situation where susceptible humans are
							infected with the virus when the infectious
							mosquitoes feed from them. The dynamics of the
							cross-transmission is captured by these two arrows,
							which also connect the SEI system of mosquitoes and
							the SEIR system of humans.
						</p>
					</section>
				</div>

				<div className='pb-12'>
					<section id='s4'>
						<h2 className='text-3xl font-bold text-[#C94C67]'>
							Results and Discussions
						</h2>
						<p className='mt-2 text-gray-700'>
							Through this complex ODE system, we were able to
							capture the complete dynamics of all 10 compartments
							in this mosquito-human cross-transmission process.
							The figure below shows the holistic view of the
							system running without any control and manipulation
							of parameters.
						</p>
						<img src={model2Img} alt='result-pic'></img>
						<li className='mt-2 text-gray-700'>
							In the aquatic phase, egg, larva, and pupae all
							eventually grow to their capacity.
						</li>
						<img src={model3Img} alt='result-pic'></img>
						<li className='mt-2 text-gray-700'>
							Adult mosquitoes experienced a round of infection
							slightly after the outbreak in the human population,
							with the peak happening on around the 120th day.
							However, the mosquito population quickly recovers
							from the disease, and the number of infected
							mosquitoes approaches 0.
						</li>
						<img src={model4Img} alt='result-pic'></img>
						<li className='mt-2 text-gray-700'>
							Humans experience the outbreak slightly before
							mosquitoes, with the peak at around 100th day.
							Because of the assumption that there are no deaths
							from dengue and the recovered population will never
							contract the same strain of dengue again, the human
							population eventually recovers completely.
						</li>
						<p className='mt-2 text-gray-700'>
							To observe the impact of our sticky ovitraps, we aim
							to link the connection between the larvae population
							and the infected human population. In our first
							simulation, we alter three different numbers of
							sticky ovitraps implemented in a location with a
							human population of 2500 and 15000 mosquitoes to
							show different levels of severance in terms of the
							infected human population. The simulation was run
							with the software Rstudio, and the impact of our
							sticky ovitraps is clear from the following figure:
						</p>
						<img src={model5Img} alt='result-pic'></img>
						<p className='mt-2 text-gray-700'>
							When more sticky ovitraps are introduced, we observe
							a drop in the larva population and a substantial
							decrease in the peak number of infected humans. The
							model shows that in this neighborhood of 2500 human
							population filled with 15000 mosquitoes, when around
							450 of our sticky ovitraps with the synthetic
							larvicidal toxins are introduced, we can lower the
							dengue fever cases down to 50% .
						</p>

						<div className='overflow-x-auto max-w-lg mx-auto p-6'>
							<table className='min-w-full table-auto border-collapse border border-gray-300'>
								<tbody>
									<tr>
										<th className='border border-gray-300 px-4 py-2'>
											Number of Sticky Ovitraps
											Implemented
										</th>
										<th className='border border-gray-300 px-4 py-2'>
											Ovitrap = 0
										</th>
										<th className='border border-gray-300 px-4 py-2'>
											Ovitrap = 200
										</th>
										<th className='border border-gray-300 px-4 py-2'>
											Ovitrap = 450
										</th>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											Larva death rate by synthetic
											larvicidal toxin & Sticky Ovitraps
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											0
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											0.6
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											1.2
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											Infected human population
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											396
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											310
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											192
										</td>
									</tr>
								</tbody>
							</table>
						</div>
						<p className='mt-2 text-gray-700'>
							When developing the model, we had the opportunity to
							discuss our results with the leading experts in
							vector-borne diseases, Dr. Chen and Dr. Tsai. We
							learned that scientists are still struggling to
							develop universal vaccines for dengue fever, and
							it’s due to several reasons. A successful vaccine
							must provide immunity against all four serotypes to
							prevent the disease effectively. Achieving balanced
							immunity is complex, while partial immunity against
							one or two serotypes lead to severe vulnerabilities
							against the other serotypes due to complications
							such as antibody-dependent enhancement. Current
							vaccines, such as Dengvaxia, result in severe
							adverse side effects and low effectiveness. The
							dengue virus is also constantly evolving and
							mutating, which makes the vaccines ineffective.
						</p>
						<p className='mt-2 text-gray-700'>
							So we decided to run a second simulation to mimic
							the effect of a vaccine with 80% efficacy across
							different vaccination percentages in an area of 2500
							human population: 0%, 25%, 50% vaccinated.
						</p>
						<img src={model6Img} alt='result-pic'></img>
						<img src={model7Img} alt='result-pic'></img>
						<p className='mt-2 text-gray-700'>
							From the second simulation, it’s shown that when a
							certain percentage of the population is vaccinated,
							it’s removed from the susceptible human population
							compartment to the recovered human population
							compartment. Logically, as the simulation shows, the
							infected human population peaks at a lower summit
							when a larger portion of the public is vaccinated.
						</p>
						<p className='mt-2 text-gray-700'>
							Interestingly, implementing 450 sticky ovitraps in a
							population of 2500 people achieves an effect
							equivalent to a vaccination campaign with 80%
							efficacy that vaccinates 50% of the human
							population. Considering the difficulty in developing
							an effective dengue vaccine as established earlier,
							this equivalence demonstrates the effectiveness of
							our sticky ovitraps not just in disease control but
							also in economic efficiency.
						</p>
					</section>
				</div>

				<div className='pb-12'>
					<section id='s5'>
						<h2 className='text-3xl font-bold text-[#C94C67]'>
							Try out the Model!
						</h2>
						{<Graph1 />}
						<br></br>
					</section>
				</div>
				<div className='pb-12'>
					<section id='s16'>
						<h2 className='text-3xl font-bold text-[#C94C67]'>
							Methods and Assumptions
						</h2>
						<p className='mt-2 text-gray-700'>
							The first four assumptions are made considering the fact that we are not looking at second infections. Because cross-transmission and the type of dengue fever caught only affect reinfections, there is no effect on the first infection. Thus, using the first four assumptions, we simplified the model without causing differences in our results.
						</p>
						<ol style={{ listStyleType: 'decimal' }}>
							<li className='mt-2 text-gray-700'>
								Our model considers the cross-transmission of
								dengue fever disease within one virus serotype.
							</li>
							<li className='mt-2 text-gray-700'>
								We assume that the birthrate and mortality rates
								for humans are equal, meaning the total human
								population is constant.
							</li>
							<li className='mt-2 text-gray-700'>
								Our model assumes that all individuals who have
								recovered from dengue are immune, meaning we are
								not looking at second infections.
							</li>
							<li className='mt-2 text-gray-700'>
								Our model does not take into account
								disease-induced mortality.
							</li>
							<li className='mt-2 text-gray-700'>
								We assume that the chance of infection from a
								bite equals the percentage of humans/mosquitoes
								that are infectious.
							</li>
							<li className='mt-2 text-gray-700'>
								We assume the condition to be favorable for
								mosquitoes as it most accurately represents the
								situation when dengue fever is most prevalent.
							</li>
							<li className='mt-2 text-gray-700'>
								We assume half of the mosquitoes caught are
								female, as the mosquito sex ratio is proven to be
								1:1.
							</li>
							<li className='mt-2 text-gray-700'>
								We assume all female mosquitoes will lay eggs.
							</li>
						</ol>
					</section>
				</div>

				<div className='pb-12'>
					<section id='s6'>
						<h2 className='text-3xl font-bold pb-8 text-[#C94C67]'>
							Equations and Parameters
						</h2> 
						<div className='flex flex-col items-center p-6'>
							<h1 className='text-xl font-bold mb-4'>
								The System of Differential Equations
							</h1>
							<div className='w-full md:w-3/4 p-4 bg-[#FFFBF2] rounded-lg shadow-lg'>
								<BlockMath
									math={`\\frac{d}{dt}E(t) = \\phi\\left[1 - \\frac{E(t)}{C'}\\right]M(t) - (\\sigma_e + \\mu_e)E(t)`}
								/>
								<BlockMath
									math={`\\frac{d}{dt}L(t) = \\sigma_eE(t) - (\\sigma_l + \\mu_l + \\mu_l')L(t)`}
								/>
								<BlockMath
									math={`\\frac{d}{dt}P(t) = \\sigma_lL(t) - (\\sigma_p + \\mu_p)P(t)`}
								/>
								<BlockMath
									math={`\\frac{d}{dt}M_s(t) = \\sigma_pP(t) - \\left[\\beta_M \\frac{I(t)}{N} + \\mu_M + \\mu_M'\\right]M_s(t)`}
								/>
								<BlockMath
									math={`\\frac{d}{dt}M_e(t) = \\beta_M \\frac{I(t)}{N}M_s(t) - [\\gamma_M + \\mu_M + \\mu_M']M_e(t)`}
								/>
								<BlockMath
									math={`\\frac{d}{dt}M_i(t) = \\gamma_MM_e(t) - [\\mu_M + \\mu_M']M_i(t)`}
								/>
								<BlockMath
									math={`\\frac{d}{dt}s(t) = \\mu_h - \\left[\\beta_h \\frac{M_i(t)}{M(t)} + \\mu_h\\right]s(t)`}
								/>
								<BlockMath
									math={`\\frac{d}{dt}e(t) = \\left[\\beta_h \\frac{M_i(t)}{M(t)}\\right]s(t) - (\\gamma_h + \\mu_h)e(t)`}
								/>
								<BlockMath
									math={`\\frac{d}{dt}i(t) = \\gamma_he(t) - (\\sigma_h + \\mu_h)i(t)`}
								/>
								<BlockMath
									math={`\\frac{d}{dt}r(t) = \\sigma_hi(t) - \\mu_hr(t)`}
								/>
							</div>
						</div>
						<p className='mt-2 text-gray-700'>
							Table 3. Parameters we used for our epidemiology model.
						</p>
						<div className='overflow-x-auto'>
							<table className='min-w-full table-auto border-collapse border border-gray-300'>
								<thead>
									<tr>
										<th className='border border-gray-300 px-4 py-2'>
											Parameters
										</th>
										<th className='border border-gray-300 px-4 py-2'>
											Description
										</th>
										<th className='border border-gray-300 px-4 py-2'>
											Value
										</th>
										<th className='border border-gray-300 px-4 py-2'>
											Unit
										</th>
										<th className='border border-gray-300 px-4 py-2'>
											Source
										</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											ϕ
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Mosquito Birth Rate
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											1
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											/day
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Sylvestre Aureliano Carvalho et al.
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											σₑ
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Transition Rate: Egg → Larva
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											0.33
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											/day
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Sylvestre Aureliano Carvalho et al.
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											σₗ
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Transition Rate: Larva → Pupa
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											0.14
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											/day
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Sylvestre Aureliano Carvalho et al.
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											σₚ
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Transition Rate: Pupa → Adult
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											0.346
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											/day
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Sylvestre Aureliano Carvalho et al.
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											μₑ
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Mosquito Egg Death Rate
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											0.05
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											/day
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Sylvestre Aureliano Carvalho et al.
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											μₗ
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Natural Mosquito Larva Death Rate
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											0.05
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											/day
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Sylvestre Aureliano Carvalho et al.
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											μₗ'
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Larva Death Rate by our larvicidal toxin
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											0.62
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											/day
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Estimated from wetlab data
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											μₚ
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Mosquito Pupa Death Rate
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											0.0167
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											/day
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Sylvestre Aureliano Carvalho et al.
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											μₘ
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Natural Adult Mosquito Death Rate
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											0.042
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											/day
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Sylvestre Aureliano Carvalho et al.
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											μₘ'
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Adult Mosquito Death Rate by our larvicidal toxin
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											0.02
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											/day
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Estimated from wetlab data
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											μₕ
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Human Death Rate
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											0.0000365
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											/day
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Assumption
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											βₘ
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Contact rateinfection of Susceptible Mosquitoes
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											0.75
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											/day
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Sylvestre Aureliano Carvalho et al.
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											βₕ
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Contact rateinfection of Infectious Mosquitoes
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											0.75
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											/day
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Sylvestre Aureliano Carvalho et al.										
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											γₘ
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Mosquito Incubation Rate
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											0.2
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											/day
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Sylvestre Aureliano Carvalho et al.
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											γₕ
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Human Incubation Rate
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											0.1
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											/day
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Sylvestre Aureliano Carvalho et al.
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											σₕ
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Human Recovery Rate
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											0.143
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											/day
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Sylvestre Aureliano Carvalho et al.
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											C'
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Egg Capacity
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											7500
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Capita
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Liao et al., 2015
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											k
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Number of larvae per human initially 
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											3
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											/human
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Liao et al., 2015
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											m
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Female mosquitoes per human initially
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											6
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											/human
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Liao et al., 2015
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											t₁
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Time required to reach 50%
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											1062.658
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											minute
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Estimated from wetlab data
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											M<sub>ovi-attract</sub>
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Amount of mosquitoes attracted by an
											ovitrap
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											20
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Capita
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Estimated from wetlab data
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											Ovi<sub>max</sub>
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Maximum number of mosquitoes in the
											ovitrap
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											70
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Capita
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Estimated from wetlab data
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											P<sub>stuck</sub>
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Chance of a mosquito sticking into
											an ovitrap
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											0.08
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											N/A
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Educated estimate from wet lab
											data
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											N<sub>mos</sub>
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Mosquito Population
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											15000
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Capita
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Liao et al., 2015
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											N<sub>h</sub>
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Human Population
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											2500
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Capita
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Assumption
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											f<sub>biting</sub>
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Biting Frequency
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											0.5
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											bites/human/day
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Estimated from wetlab data
										</td>
									</tr>
								</tbody>
							</table>
						</div>
						<p className='pl-48 pt-16 text-lg text-[#C94C67]'>
							<strong>Table 4.</strong> Initial Values
						</p>
						<div className='overflow-x-auto max-w-md mx-auto'>
							<table className='min-w-full table-auto border-collapse border border-gray-300'>
								<thead>
									<tr>
										<th className='border border-gray-300 px-4 py-2'>
											Parameter
										</th>
										<th className='border border-gray-300 px-4 py-2'>
											Value
										</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											Egg
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											5000
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											Larva
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											2000
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											Pupa
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											0
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											S_mos
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											15000
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											E_mos
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											0
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											I_mos
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											0
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											S_hum
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											2499
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											E_hum
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											0
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											I_hum
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											1
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											R_hum
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											0
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</section>
				</div>
				<div className='pb-12'>
					<section id='s17'>
						<h2 className='text-3xl font-bold text-[#C94C67]'>
							Parameter Estimations from Wetlab Data
						</h2>
						<div className='overflow-x-auto max-w-4xl mx-auto pb-8'>
							<table className='min-w-full table-auto border-collapse border border-gray-300'>
								<thead>
									<tr>
										<th className='border border-gray-300 px-4 py-2'>
											Parameter
										</th>
										<th className='border border-gray-300 px-4 py-2'>
											Description
										</th>
										<th className='border border-gray-300 px-4 py-2'>
											Value
										</th>
										<th className='border border-gray-300 px-4 py-2'>
											Unit
										</th>
										<th className='border border-gray-300 px-4 py-2'>
											Source
										</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											t<sub>1</sub>
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											The time required to reach a 50%
											accumulated mortality rate
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											1062.658
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											minute
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Estimated from wetlab data
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											M<sub>ovi-attract</sub>
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Amount of mosquitoes attracted by an
											ovitrap
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											20
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Mosquitoes per ovitrap
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Estimated from wetlab data
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											Ovi<sub>max</sub>
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Maximum number of mosquitoes in the
											ovitrap
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											70
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Mosquitoes per ovitrap
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Estimated from wetlab data
										</td>
									</tr>
									<tr>
										<td className='border border-gray-300 px-4 py-2'>
											P<sub>stuck</sub>
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Number of mosquitoes that get stuck
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											0.08
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											N/A
										</td>
										<td className='border border-gray-300 px-4 py-2'>
											Educated Estimated from wetlab data
										</td>
									</tr>
								</tbody>
							</table>
						</div>
						<p className='mt-2 text-gray-700'>
							In this section, we look at the methods utilized for
							parameter estimation, the data sources used, and the
							significance of accurate parameter values in the
							context of our epidemiological model. This is to
							more accurately model the effect of our synthetic
							larvicidal toxin, and its impact on the spread of
							dengue disease. Instead of using parameters from
							literature, we used data from Wetlab’s experiments
							to obtain more accurate and representative results.
						</p>
						<p className='mt-2 text-gray-700'>
							Thus, we decided to model the parameters as a
							function of the number of sticky ovitraps to reflect
							a realistic effect of our product. The parameters
							that show significant changes with the number of
							sticky ovitraps include the mortality rate of
							larvae, the mortality rate of adult mosquitoes,
							daily biting rate, and the maximum larva capacity in
							a sticky ovitrap.
						</p>
						<p className='mt-2 text-gray-700'>
							The methods used for parameter estimation include an
							exponential regression, whereby, through data
							provided by Wetlab, we worked out the time taken to
							kill off 50% of the larvae within the sticky
							ovitraps. This value is referred to as LC50 in
							reference papers. After data collection, we plotted the
							data points and fit it with an exponential regression
							model to work out the time required for 100%
							mortality of larvae.
						</p>
						<p className='mt-2 text-gray-700'>
							We obtained key pieces of data from our Wetlab team
							during functional assays:
						</p>
						<p className='mt-2 text-gray-700'>
							t<sub>1</sub>: the time required to reach a 50%
							accumulated larva and adult mosquito mortality rate
							within one trial containing 20 mosquitoes
						</p>
						<p className='mt-2 text-gray-700'>
							M<sub>ovi-attract</sub>: the number of mosquitoes
							attracted by a sticky ovitrap per day.
						</p>
						<p className='mt-2 text-gray-700'>
							N<sub>mos-ovi</sub>: The total number of mosquitoes
							stuck in the sticky ovitrap.
						</p>
						<p className='mt-2 text-gray-700'>
							P<sub>stuck</sub>: Probability of a mosquito
							sticking onto a sticky ovitrap.
						</p>
						<p className='mt-2 text-gray-700 font-bold'>
							t<sub>1</sub> :Time Required for a 50%
							Accumulated Mortality Rate (LC50){' '}
						</p>
						<p className='mt-2 text-gray-700'>
							In our differential equations, the natural death
							rate for humans and mosquitoes is the inverse of the
							number of days. So, in order to estimate the
							parameters of the mortality rate of larvae and adult
							mosquitoes, we attempted to find the amount of time,
							in minutes, it takes to reach a 50% accumulated
							mortality rate for larvae (t<sub>1</sub>). To
							achieve this, we collected the accumulated
							percentage of larvae killed after a certain amount
							of time by conducting experiments using our
							synthetic larvicidal toxin from Wetlab. This enabled
							us to fit our data into an exponential equation that
							plateaus near 100% and to find the time when 50% of
							larvae are killed.
						</p>
						<table className='min-w-full table-auto border-collapse border border-gray-300'>
							<thead>
								<tr>
									<th
										style={{
											border: '1px solid black',
											padding: '10px',
										}}
									>
										Time (min)
									</th>
									<th
										style={{
											border: '1px solid black',
											padding: '10px',
										}}
									>
										Accumulated Mortality Rate
									</th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td
										style={{
											border: '1px solid black',
											padding: '10px',
										}}
									>
										0
									</td>
									<td
										style={{
											border: '1px solid black',
											padding: '10px',
										}}
									>
										0
									</td>
								</tr>
								<tr>
									<td
										style={{
											border: '1px solid black',
											padding: '10px',
										}}
									>
										360
									</td>
									<td
										style={{
											border: '1px solid black',
											padding: '10px',
										}}
									>
										15
									</td>
								</tr>
								<tr>
									<td
										style={{
											border: '1px solid black',
											padding: '10px',
										}}
									>
										720
									</td>
									<td
										style={{
											border: '1px solid black',
											padding: '10px',
										}}
									>
										20
									</td>
								</tr>
								<tr>
									<td
										style={{
											border: '1px solid black',
											padding: '10px',
										}}
									>
										1080
									</td>
									<td
										style={{
											border: '1px solid black',
											padding: '10px',
										}}
									>
										60
									</td>
								</tr>
								<tr>
									<td
										style={{
											border: '1px solid black',
											padding: '10px',
										}}
									>
										1440
									</td>
									<td
										style={{
											border: '1px solid black',
											padding: '10px',
										}}
									>
										80
									</td>
								</tr>
								<tr>
									<td
										style={{
											border: '1px solid black',
											padding: '10px',
										}}
									>
										1800
									</td>
									<td
										style={{
											border: '1px solid black',
											padding: '10px',
										}}
									>
										80
									</td>
								</tr>
								<tr>
									<td
										style={{
											border: '1px solid black',
											padding: '10px',
										}}
									>
										2160
									</td>
									<td
										style={{
											border: '1px solid black',
											padding: '10px',
										}}
									>
										99
									</td>
								</tr>
							</tbody>
						</table>
						<p className='mt-2 text-gray-700'>
							With these values obtained from wetlab data, we were
							able to derive our equation through an exponential
							regression model.
						</p>
						<p className='mt-2 text-gray-700'>
							Equation format:{' '}
							<i>
								f(t) = ab<sup>t</sup> + C
							</i>
						</p>
						<table className='min-w-full table-auto border-collapse border border-gray-300'>
							<tbody>
								<tr>
									<td
										style={{
											border: '1px solid black',
											padding: '10px',
										}}
									>

									</td>
									<td
										style={{
											border: '1px solid black',
											padding: '10px',
										}}
									>
										Trial 1
									</td>
								</tr>
								<tr>
									<td
										style={{
											border: '1px solid black',
											padding: '10px',
										}}
									>
										a
									</td>
									<td
										style={{
											border: '1px solid black',
											padding: '10px',
										}}
									>
										-413.129
									</td>
								</tr>
								<tr>
									<td
										style={{
											border: '1px solid black',
											padding: '10px',
										}}
									>
										b
									</td>
									<td
										style={{
											border: '1px solid black',
											padding: '10px',
										}}
									>
										1.00013
									</td>
								</tr>
								<tr>
									<td
										style={{
											border: '1px solid black',
											padding: '10px',
										}}
									>
										c
									</td>
									<td
										style={{
											border: '1px solid black',
											padding: '10px',
										}}
									>
										409.827
									</td>
								</tr>
							</tbody>
						</table>
						<p className='mt-2 text-gray-700'>
							This gives us the final equation:{' '}
							<i>
								f(t) = -413.129 × 1.00013<sup>t</sup> + 409.827
							</i>
						</p>
						<p className='mt-2 text-gray-700'>
							To find the time it takes to reach 50% accumulation,
							we assume that f(t) = 50
						</p>
						<p className='mt-2 text-gray-700'>
							By rearranging this equation to solve for t, we get:
						</p>
						<p>
							<i>
								t = -log<sub>b</sub> [(50 - C) / a]
							</i>
						</p>
						<p>
							<i>
								t = -log<sub>1.00013</sub> [(50 - 409.827) /
								-413.129]
							</i>
						</p>
						<p>t = 1062.658 minutes ≈ 17.7 hours</p>
						<p className='mt-2 text-gray-700'>
							Thus, we concluded that it takes approximately 17.7
							hours to reach a 50% accumulated larva.
						</p>
						<p className='mt-2 text-gray-700 font-bold'>
							Mortality Rate of Larva Caused by our Synthetic
							Larvicidal Toxin
						</p>
						<p className='mt-2 text-gray-700'>
							Using the time required to reach a 50% accumulated
							mortality rate, we were able to derive the mortality
							rate of larva caused by our toxin. To stay
							consistent with the rest of the parameters, we
							converted the unit of time (
							<i>
								t<sub>1</sub>
							</i>
							) from minutes to days,{' '}
							<i>
								t<sub>1</sub>/1440
							</i>
							. The lifespan of larvae in the sticky ovitraps is
							represented by{' '}
							<i>
								t<sub>1</sub>/1440
							</i>
							. We assumed that the population of the larva is{' '}
							<i>k</i> times that of the human population (
							<i>
								N<sub>h</sub>
							</i>
							), and the number of mosquitoes caught in a sticky
							ovitrap is constant. Therefore,{' '}
							<i>
								N<sub>h</sub> × k / Ovi<sub>max</sub>
							</i>{' '}
							demonstrates the number of sticky ovitraps needed to
							kill a specific population of mosquitoes. By
							dividing this by the number of sticky ovitraps
							implemented, we get a ratio representing how many
							rounds the ovitraps need to go through to kill all
							mosquitoes:
							<i>
								(N<sub>h</sub> × 3 / Ovi<sub>max</sub>) × (1 / N
								<sub>ovi</sub>)
							</i>
							. This means that{' '}
							<i>
								(1 + t<sub>1</sub> / 1440) × (N<sub>h</sub> × k)
							</i>{' '}
							represents the total time to kill{' '}
							<i>
								N<sub>mos</sub>
							</i>
							. The mortality rate of the larva (
							<i>
								μ'<sub>l</sub>
							</i>
							) is the reciprocal of the total time to kill{' '}
							<i>
								N<sub>mos</sub>
							</i>
							, which gives the final equation:
						</p>
						<p>

							Total time to kill N<sub>mos</sub> (days):

							<br />
							<i>
								= (1 + t<sub>1</sub>/1440) × (N<sub>h</sub> × k)
								/ (Ovi<sub>max</sub> × N<sub>ovi</sub>)
							</i>
						</p>
						<p>

							Mortality rate of larva (μ'<sub>l</sub>):

							<br />
							<i>
								= (Ovi<sub>max</sub> × N<sub>ovi</sub>) / (1 + t
								<sub>1</sub>/1440) × N<sub>h</sub> × k
							</i>
						</p>
						<p className='mt-2 text-gray-700 font-bold'>
							Mortality Rate of Adult Mosquito Caused by Our
							Sticky Ovitrap
						</p>
						<p className='mt-2 text-gray-700'>
							Similar to the mortality rate of larvae, the data we
							used to find the mortality rate of adult mosquitoes
							also includes the time it takes to reach a 50%
							accumulated mortality rate (
							<i>
								t<sub>1</sub>
							</i>
							). We divided{' '}
							<i>
								t<sub>1</sub>
							</i>{' '}
							by 1440 to represent a unit conversion from minutes
							to days. However, instead of adding 1 to{' '}
							<i>
								t<sub>1</sub> / 1440
							</i>
							, we added 4 as it takes four days to transform from
							an egg to an adult mosquito. Thus,{' '}
							<i>
								4 + t<sub>1</sub> / 1440
							</i>{' '}
							represents the lifespan of adult mosquitoes in
							sticky ovitraps. We assumed the mosquito population
							to be a factor of the human population (
							<i>
								N<sub>h</sub>
							</i>
							), so, <i>m</i>, which represents female mosquitoes
							per human, is multiplied with{' '}
							<i>
								N<sub>h</sub>
							</i>{' '}
							to represent the total adult mosquito population.{' '}
							<i>
								M<sub>ovi-attract</sub>
							</i>{' '}
							is the amount of mosquitoes that are attracted to
							the sticky ovitrap: the product of{' '}
							<i>
								M<sub>ovi-attract</sub>
							</i>{' '}
							and the percentage of mosquitoes stuck onto one
							sticky ovitrap (
							<i>
								P<sub>stuck</sub>
							</i>
							) is the number of mosquitoes that are stuck onto
							one single sticky ovitrap. Therefore, by multiplying
							this by the total number of sticky ovitraps (
							<i>
								N<sub>ovi</sub>
							</i>
							), we are able to determine the total number of
							mosquitoes stuck onto the total number of sticky
							ovitraps. Thus, we derived the mortality rate of
							adult mosquitoes:
							<p>
								<i>
									μ<sub>M</sub>'(days<sup>-1</sup>) = (M
									<sub>ovi-attract</sub> × P<sub>stuck</sub> ×
									N<sub>ovi</sub>) / ((4 + t<sub>1</sub>/1440)
									× N<sub>h</sub> × m)
								</i>
							</p>
						</p>
						<p className='mt-2 text-gray-700 font-bold'>
							Daily Biting Rate
						</p>
						<p className='mt-2 text-gray-700'>
							To account for the change in biting rate due to our ovitraps, we considered the mosquitoes trapped by our sticky ovitraps.
							We multiplied the number of mosquitoes attracted by one of our ovitraps (<i>M<sub>ovi-attract</sub></i>) by the probability
							of our sticky ovitrap trapping a mosquito (<i>P<sub>stuck</sub></i>) and the total number of ovitraps in a certain area
							(<i>N<sub>ovi</sub></i>), giving us the total number of mosquitoes stuck by our ovitraps.

							To find the final daily biting frequency, we multiplied the biting frequency (<i>f<sub>biting</sub></i>) by the percentage
							of mosquitoes not trapped due to our ovitraps. To find this percentage, we divided the total number of mosquitoes trapped
							(<i>M<sub>ovi-attract</sub> &times; P<sub>stuck</sub> &times; N<sub>ovi</sub></i>) by the total mosquito population
							<i>M(t)</i>, which gives the percentage of mosquitoes trapped.

							<i>1 - (M<sub>ovi-attract</sub> &times; P<sub>stuck</sub> &times; N<sub>ovi</sub> / M(t))</i> then gives the percentage
							of mosquitoes not trapped. By multiplying this result by <i>f<sub>biting</sub></i>, we get the final equation for the daily biting rate.
						</p>

						<p className='text-center'>

							Daily biting rate = (1 - (M
							<sub>ovi-attract</sub> × P<sub>stuck</sub> × N
							<sub>ovi</sub>) / (N<sub>h</sub> × m)) × f
							<sub>biting</sub>
						</p>
					</section>
				</div>

				<div className='pb-12'>
					<section id='s7'>
						<h2 className='text-3xl font-bold text-[#C94C67]'>
							Limitations and Future Directions
						</h2>
						<p className='mt-2 text-gray-700'>
							In our model, the mosquitoes never recover from the
							infection since their infective period ends with
							their death. This may allow us to focus on the macro
							aspects of the system but miss out on intricate
							phenomena happening at a micro-scale. The lack of
							seasonal variations in our model is also a
							limitation. We assumed the temperature and
							environment to be favorable for mosquitoes. In our
							current model, it takes around 100 days for the
							number of infected humans to peak. In reality,
							favorable periods would not last until the number of
							infected humans and mosquitoes reaches its peak. By
							incorporating these fluctuations, the accuracy of
							our model could greatly improve.
						</p>
						<p className='mt-2 text-gray-700'>
							We plan on considering second infections in our
							model. This would mean the addition of another SEI
							compartment specifically for second infections to
							the already existing model and a transformation rate
							from the Recovered to the Susceptible after
							infection compartment. This additional SEI
							compartment would have identical infection rates,
							transition rates, and death rates except for the
							death rate of the infected human population, which
							would be greater due to a higher risk of
							experiencing severe symptoms after a second
							infection. Combined with our findings in this model,
							we can then better measure the impact of our
							synthetic larvicidal toxin and sticky ovitraps on
							the reality of dengue fever disease.
						</p>
					</section>
				</div>
				<div className='pb-12'>
					<section id='s18'>
						<h2 className='text-3xl font-bold text-[#C94C67]'>
							Contribution
						</h2>
						<p className='mt-2 text-gray-700'>
							We want to first thank the 2020 overgrad Leiden Team
							for outsourcing their Rstudio model codes for
							COVID-19. It was of great assistance to the
							development of our dengue fever model. Special
							thanks to the drylab team member Sebastian Tandar,
							who was kind enough to reply to our emails 4 years
							after their success in iGEM 2020!
						</p>
						<p className='mt-2 text-gray-700'>
							Due to the lack of foundation of Rstudio coding as
							highschool students, we still went through many
							brain-befuddling revelations in order to adapt their
							model to our mathematical system. This is why we
							also wanted to contribute by outsourcing our model
							and parameters. With our new adaptations, future
							iGEM teams could utilize this model to investigate
							future trends of disease spread.
						</p>
						<p className='mt-2 text-gray-700 font-bold'>
							Run the model yourself! (Rstudio)
						</p>
						<ol style={{ listStyleType: 'decimal' }}>
							<li className='mt-2 text-gray-700'>
								Download and save the .txt and .csv files into
								the same folder. Change the extension of the
								.txt file to .R, and change the extension of the .csv file into .xlsl.
							</li>
							<li className='mt-2 text-gray-700'>
								Copy of the pathname (⌥⌘C for Mac & right click
								folder then choose “copy as path” for Windows)
								of the folder where the .R and .xlsl files are
								saved.
							</li>
							<li className='mt-2 text-gray-700'>
								In order to run the model, the following
								packages must be installed: readxl, deSolve,
								ggplot2, gridExtra, reshape2.
							</li>
							<li className='mt-2 text-gray-700'>
								Paste the pathname of the folder into the second
								line of the code.
							</li>
							<li className='mt-2 text-gray-700'>
								Run the code (⌘⌥R for Mac & Ctrl A then press
								“Run” for Windows) and the plots would appear in
								the “Plots” tab
							</li>
						</ol>
						<br />
						<p className='mt-2 text-gray-700'>
							The model may be manipulated by changing the values
							in the .xlsl file
						</p>
						<a href="https://static.igem.wiki/teams/5066/drylab/dengue-parameters-xlsx-parameters.csv" target="_blank" rel="noreferrer" class="text-blue-500 hover:text-blue-700 underline">
							Dengue parameters.csv
						</a>
						<br />
						<a href="https://static.igem.wiki/teams/5066/drylab/dengue-model.pdf" target="_blank" rel="noreferrer" class="text-blue-500 hover:text-blue-700 underline">
							Dengue Model.txt
						</a>

					</section>
				</div>
				<div className='pb-12'>
					<section id='s8'>
						<h2 className='text-3xl font-bold text-[#C94C67]'>
							References
						</h2>
						<p className='mt-2 text-[#6C4E52]'>
							Carvalho, S. A., da Silva, S. O., & Charret, I. D.
							C. (2019). Mathematical modeling of dengue epidemic:
							control methods and vaccination strategies. Theory
							in biosciences = Theorie in den Biowissenschaften,
							138(2), 223–239.
							<a href='https://doi.org/10.1007/s12064-019-00273-7'>
								https://doi.org/10.1007/s12064-019-00273-7
							</a>
						</p>
						<p className='mt-2 text-[#6C4E52]'>
							Liao, C. M., Huang, T. L., Cheng, Y. H., Chen, W.
							Y., Hsieh, N. H., Chen, S. C., & Chio, C. P. (2015).
							Assessing dengue infection risk in the southern
							region of Taiwan: implications for control.
							Epidemiology and infection, 143(5), 1059–1072.
							<br />
							<a href='https://doi.org/10.1017/S0950268814001745'>
								https://doi.org/10.1017/S0950268814001745
							</a>
						</p>
						<p className='mt-2 text-[#6C4E52]'>
							Yang, H. M., & Ferreira, C. P. (2008). Assessing the
							effects of vector control on dengue transmission.
							Applied Mathematics and Computation, 198(1),
							401–413.
							<br />
							<a href='https://doi.org/10.1016/j.amc.2007.08.046'>
								https://doi.org/10.1016/j.amc.2007.08.046
							</a>
						</p>
						<p className='mt-2 text-[#6C4E52]'>
							2020 Leiden Team:
							<a href='https://2020.igem.org/Team:Leiden'>
								https://2020.igem.org/Team:Leiden
							</a>
						</p>
					</section>
				</div>
			</div>
		</div >
	);
};

export default ModelTab1;
